import {useCallback, useMemo} from 'react'
import {MbscCalendarDayData, MbscCalendarEvent, MbscResource} from '@mobiscroll/react'
import {Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {getDefinedMbscResources} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {toJsDate} from "crqlar-react-common/dist/utils/calendarTimezone";
import {DateTime} from "luxon";

/*
    Resource occupancy calculation hook

    We calculate calendar resource occupancy on frontend and not backend because:
    - Mobiscroll calendar creates 1 call per time slot
    - Backend calls would be too many and too slow
    - Frontend has all the data already loaded in memory

    We create maps of occupancy percentages by time slot based on events and resources
    Reason: Calculating occupancy on the fly for each slot cell is too slow
*/

interface UseOccupancyProps {
    mbscResources: MbscResource[] | null | undefined
    events: MbscCalendarEvent[] | null | undefined
    timelineStep: number
    sector: Sector | undefined
    // The day the timeline currently renders, when it renders exactly one day.
    // Only needed to resolve the hour columns of a DST switch day, see getHeaderSlot below.
    renderedDay?: Date | null
}

interface UseOccupancyReturn {
    totalActiveTableCount: number
    occupancyBySlot: Map<string, string>
    getOccupancy: (date: Date) => string
    getOccupancyFromEvents: (events: MbscCalendarEvent[]) => string
    getOccupancyClass: (occupancy: number) => string
    renderHourFooter: (args: MbscCalendarDayData) => JSX.Element
    renderHour: (args: MbscCalendarDayData) => JSX.Element
}

const FOOTER_CLASSNAME = "md-date-footer"
const HEADER_CLASSNAME = "md-date-header-hour"

const MS_PER_MINUTE = 60 * 1000
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE

// Wall clock below is the organization's (luxon default zone) - the calendar renders in that zone,
// not in the device's one (see calendarTimezone.ts in common)

// "YYYY-M-D-" prefix of every slot key of that day (month 0 based). CalendarHeader filters by it.
export const slotDayPrefix = (day: Date): string => {
    const dt = DateTime.fromJSDate(day)
    return `${dt.year}-${dt.month - 1}-${dt.day}-`
}

// Slot key from a day plus the minutes since that day's midnight (wall clock, not elapsed time)
// Format: "YYYY-M-D-H-m" in the organization's time. Keep the "YYYY-M-D-" prefix stable (slotDayPrefix).
const buildSlotKey = (day: Date, minutesOfDay: number, timelineStep: number): string => {
    // Normalize to slot boundary
    const normalizedTotalMinutes = Math.floor(minutesOfDay / timelineStep) * timelineStep

    const normalizedHours = Math.floor(normalizedTotalMinutes / 60)
    const normalizedMinutes = normalizedTotalMinutes % 60

    return `${slotDayPrefix(day)}${normalizedHours}-${normalizedMinutes}`
}

// Events carry real instants and Mobiscroll positions them by their wall clock,
// so the wall clock is what identifies the column an event sits in.
const getSlotKey = (date: Date, timelineStep: number): string => {
    const dt = DateTime.fromJSDate(date)
    return buildSlotKey(date, dt.hour * 60 + dt.minute, timelineStep)
}

/*
    DST: which column does an hour header/footer date belong to?

    Mobiscroll builds the date it hands to renderHour / renderHourFooter as
    `new Date(+dayStart + columnOffset)` — it adds the column's wall clock offset as absolute
    milliseconds. Across a DST switch that instant no longer carries the wall clock of its own
    column: on 29.03.2026 the 02:00 column arrives here as 03:00 local and every column after it
    is shifted by +1h, on 25.10.2026 they are shifted back by 1h and 02:00 arrives twice.
    Everything else in Mobiscroll (grid, event positions, cell clicks, drag to create) stays on
    the naive wall clock via `new Date(y, m, d, h, min)`, so reading the wall clock of args.date
    made our header labels and occupancy lookups disagree with the calendar by one hour.

    So we never read the wall clock of args.date. We recover the offset Mobiscroll added instead:
    the elapsed real time since the midnight of the day the column belongs to.
*/
const getHeaderSlot = (headerDate: unknown, renderedDay: Date | null | undefined): { day: Date, minutesOfDay: number } => {
    // With the timezone plugin Mobiscroll hands over its own timezone aware date object
    const date = toJsDate(headerDate)

    // renderedDay first: on a spring forward day the last columns land past midnight
    // (29.03.2026 23:30 arrives as 30.03.2026 00:30) and only the rendered day resolves them.
    for (const candidate of [renderedDay, date]) {
        if (!candidate) continue

        const day = DateTime.fromJSDate(candidate).startOf('day').toJSDate()

        const elapsedMs = date.getTime() - day.getTime()
        if (elapsedMs >= 0 && elapsedMs < MS_PER_DAY) {
            return { day, minutesOfDay: Math.round(elapsedMs / MS_PER_MINUTE) }
        }
    }

    const dt = DateTime.fromJSDate(date)
    return { day: dt.startOf('day').toJSDate(), minutesOfDay: dt.hour * 60 + dt.minute }
}

export const useResourceOccupancy = ({ mbscResources, events, timelineStep, sector, renderedDay }: UseOccupancyProps): UseOccupancyReturn => {

    const slotDurationMs = useMemo(() => timelineStep * 60 * 1000, [timelineStep])

    // Memoized total count of active tables (leaf resources)
    // Recalculates only when resources change
    const totalActiveResourceCount = useMemo(() => {
        return sector?.sectorType === SectorType.RESTAURANT
            ? getDefinedMbscResources(mbscResources)?.reduce((total, r) => total + (r.children?.length || 0), 0) || 0
            : getDefinedMbscResources(mbscResources)?.length || 0
    }, [mbscResources])

    // Set of all child resource IDs for quick lookup
    // Used to filter out parent resources from occupancy calculation

    const resourceIds = useMemo(() => {
        const ids = new Set<string>()
        mbscResources?.forEach(resource => {

            if(sector?.sectorType === SectorType.RESTAURANT) {
                // If restaurant -> then the resource is a parent
                resource.children?.forEach(child => {
                    // Adding each table here
                    ids.add(child.id as string)
                })
            } else {
                ids.add(resource.id as string)
            }

        })
        return ids
    }, [mbscResources])

    // Get occupancy class based on percentage (0-100)
    // Returns class name for styling: occupancy-0, occupancy-10, ..., occupancy-100
    const getOccupancyClass = useCallback((occupancy: number): string => {
        if (occupancy === 0) return 'occupancy-0'
        if (occupancy <= 10) return 'occupancy-10'
        if (occupancy <= 20) return 'occupancy-20'
        if (occupancy <= 30) return 'occupancy-30'
        if (occupancy <= 40) return 'occupancy-40'
        if (occupancy <= 50) return 'occupancy-50'
        if (occupancy <= 60) return 'occupancy-60'
        if (occupancy <= 70) return 'occupancy-70'
        if (occupancy <= 80) return 'occupancy-80'
        if (occupancy <= 90) return 'occupancy-90'
        return 'occupancy-100'
    }, [])

    // Calculate occupancy from events array (for renderHour args.events)
    // Used when Mobiscroll provides events directly in callback args
    const getOccupancyFromEvents = useCallback((slotEvents: MbscCalendarEvent[]): string => {
        if (!totalActiveResourceCount || !slotEvents?.length) {
            return '0'
        }

        const occupiedResources = new Set<string>()
        for (const event of slotEvents) {
            // Block periods (incl. the restaurant block mirrored onto its tables) are not bookings
            if (!event.resource || event.isBlockPeriod) continue

            const resourceId = event.resource as string
            // Only count child resources (actual tables), skip parent resources
            if (!resourceIds.has(resourceId)) continue

            occupiedResources.add(resourceId)
        }

        const occupancy = (occupiedResources.size * 100) / totalActiveResourceCount
        return occupancy.toFixed(0)
    }, [totalActiveResourceCount, resourceIds])

    const occupancyBySlot = useMemo(() => {
        // Mobiscroll calls renderHourFooter once per time slot - we can't avoid that
        // But we can precompute all occupancies here in a single pass O(n)
        // Then each renderHourFooter call becomes O(1) Map lookup instead of O(n) calculation
        // This changes total complexity from O(n × m) to O(n + m), where:
        //   n = number of events
        //   m = number of time slots
        const occupancyMap = new Map<string, string>()

        if (!totalActiveResourceCount || !events?.length) {
            return occupancyMap
        }

        const eventsBySlot = new Map<string, Set<string>>()

        for (const event of events) {
            // Block periods (incl. the restaurant block mirrored onto its tables) are not bookings
            if (!event.resource || !event.start || event.isBlockPeriod) continue

            const resourceId = event.resource as string
            // Only count child resources (actual tables), skip parent resources
            if (!resourceIds.has(resourceId)) continue

            const startDate = toJsDate(event.start)
            const endDate = toJsDate(event.end)

            // Create a working copy for iteration, normalized to slot boundary
            const currentSlot = new Date(startDate)
            currentSlot.setSeconds(0, 0)
            const startMinutes = currentSlot.getMinutes()
            currentSlot.setMinutes(Math.floor(startMinutes / timelineStep) * timelineStep)

            while (currentSlot < endDate) {
                const slotKey = getSlotKey(currentSlot, timelineStep)

                if (!eventsBySlot.has(slotKey)) {
                    eventsBySlot.set(slotKey, new Set())
                }
                eventsBySlot.get(slotKey)!.add(resourceId)

                // Move to next slot
                currentSlot.setTime(currentSlot.getTime() + slotDurationMs)
            }
        }

        // Getting occupancy percentage for each slot
        eventsBySlot.forEach((resourceIds, slotKey) => {
            const occupancy = (resourceIds.size * 100) / totalActiveResourceCount
            occupancyMap.set(slotKey, occupancy.toFixed(0))
        })

        return occupancyMap
    }, [events, totalActiveResourceCount, resourceIds, timelineStep, slotDurationMs])

    const getOccupancy = useCallback((date: Date): string => {
        const slotKey = getSlotKey(toJsDate(date), timelineStep)
        return occupancyBySlot.get(slotKey) || '0'
    }, [occupancyBySlot, timelineStep])

    // Occupancy of the column an hour header / footer belongs to (DST safe, see getHeaderSlot)
    const getHeaderOccupancy = useCallback((date: Date): number => {
        const { day, minutesOfDay } = getHeaderSlot(date, renderedDay)
        return parseInt(occupancyBySlot.get(buildSlotKey(day, minutesOfDay, timelineStep)) || '0', 10)
    }, [occupancyBySlot, timelineStep, renderedDay])

    // Render footer with occupancy percentage and color-coded background
    const renderHourFooter = useCallback((args: MbscCalendarDayData) => {
        const occupancy = getHeaderOccupancy(args.date)
        return <div className={`${FOOTER_CLASSNAME}`}>{occupancy} %</div>
    }, [getHeaderOccupancy])

    // Render hour header with occupancy color-coded background
    const renderHour = useCallback((args: MbscCalendarDayData) => {
        const occupancy = getHeaderOccupancy(args.date)
        const occupancyClass = getOccupancyClass(occupancy)

        // Here we override the timeformat of the mobiscroll calendar.
        // Built from the column's own wall clock, never from args.date - see getHeaderSlot
        const { minutesOfDay } = getHeaderSlot(args.date, renderedDay)
        const timeStr = [Math.floor(minutesOfDay / 60), minutesOfDay % 60]
            .map(part => String(part).padStart(2, '0'))
            .join(':')  // always 24h

        return <div className={`${HEADER_CLASSNAME} ${occupancyClass}`}>{timeStr}</div>
    }, [getHeaderOccupancy, getOccupancyClass, renderedDay])

    return {
        totalActiveTableCount: totalActiveResourceCount,
        occupancyBySlot,
        getOccupancy,
        getOccupancyFromEvents,
        getOccupancyClass,
        renderHourFooter,
        renderHour
    }
}
